import { createContext, useContext, useState, useEffect } from "react";
import apiClient from "../config/axios";
import { jwtDecode } from "jwt-decode";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token") || null);
  const [loading, setLoading] = useState(true);

  // Define logout function before useEffect so it can be used
  const logout = () => {
    console.log('=== LOGOUT ===');
    
    localStorage.removeItem("token");
    localStorage.removeItem("clientToken");
    localStorage.removeItem("client");
    localStorage.removeItem("userData");
    localStorage.removeItem("rememberedEmail");
    localStorage.removeItem("rememberMe");
    localStorage.removeItem("lastRoute"); // Clear saved route on logout
    
    setToken(null);
    setUser(null);
    setLoading(false);
    
    console.log('Logout complete');
  };

  useEffect(() => {
    console.log("[AUTH] ====== AUTHCONTEXT INITIALIZATION ======");
    console.log("[AUTH] Checking localStorage on mount...");
    
    const t = localStorage.getItem("token");
    const u = localStorage.getItem("userData");
    
    console.log("[AUTH] Token exists:", !!t);
    console.log("[AUTH] UserData exists:", !!u);
    
    if (t) {
      console.log("[AUTH] ✅ Token found, restoring session");
      console.log("[AUTH] Token value:", t.substring(0, 30) + '...');
      setToken(t);
      
      if (u) {
        try {
          const userData = JSON.parse(u);
          setUser(userData);
          console.log("[AUTH] ✅ User restored:", userData);
        } catch (e) {
          console.error("[AUTH] ❌ Failed to parse userData:", e);
          // If userData is corrupted, try to extract from token
          try {
            const decoded = jwtDecode(t);
            const userFromToken = {
              id: decoded.userId || decoded.clientId || decoded.id,
              email: decoded.email,
              name: decoded.name,
              role: decoded.role || (decoded.clientId ? 'client' : 'user')
            };
            setUser(userFromToken);
            localStorage.setItem("userData", JSON.stringify(userFromToken));
            console.log("[AUTH] ✅ User restored from token");
          } catch (tokenErr) {
            console.error("[AUTH] ❌ Failed to decode token:", tokenErr);
          }
        }
      } else {
        // No userData, try to extract from token
        try {
          const decoded = jwtDecode(t);
          const userFromToken = {
            id: decoded.userId || decoded.clientId || decoded.id,
            email: decoded.email,
            name: decoded.name,
            role: decoded.role || (decoded.clientId ? 'client' : 'user')
          };
          setUser(userFromToken);
          localStorage.setItem("userData", JSON.stringify(userFromToken));
          console.log("[AUTH] ✅ User restored from token");
        } catch (tokenErr) {
          console.error("[AUTH] ❌ Failed to decode token:", tokenErr);
        }
      }
    } else {
      console.log("[AUTH] ❌ No token found");
    }
    
    console.log("[AUTH] Setting loading to false");
    setLoading(false);
    console.log("[AUTH] ====== AUTHCONTEXT INITIALIZED ======");
  }, []);

  const login = async (email, password) => {
    console.log("[LOGIN] ====== LOGIN FUNCTION CALLED ======");
    console.log("[LOGIN] Email:", email);
    console.log("[LOGIN] Password length:", password ? password.length : 0);
    console.log("[LOGIN] API Base URL:", apiClient.defaults.baseURL);
    console.log("[LOGIN] Starting API call...");
    
    try {
      const res = await apiClient.post("/auth/login", { email, password });
      console.log("[LOGIN] ====== API RESPONSE RECEIVED ======");
      console.log("[LOGIN] Full response:", res.data);
      console.log("[LOGIN] Response keys:", Object.keys(res.data || {}));
      console.log("[LOGIN] Has token?", !!res.data.token);
      console.log("[LOGIN] Has user?", !!res.data.user);

      const { token, user } = res.data;

      if (!token) {
        console.error("[LOGIN] ❌ NO TOKEN IN RESPONSE");
        console.error("[LOGIN] Response data:", JSON.stringify(res.data, null, 2));
        throw new Error("No token received");
      }

      if (!user) {
        console.error("[LOGIN] ❌ NO USER IN RESPONSE");
        console.error("[LOGIN] Response data:", JSON.stringify(res.data, null, 2));
        throw new Error("No user data received");
      }

      console.log("[LOGIN] ✅ Token extracted:", token.substring(0, 30) + '...');
      console.log("[LOGIN] ✅ User extracted:", user);

      // Save to localStorage FIRST (synchronous operation)
      console.log("[LOGIN] Saving to localStorage...");
      try {
        localStorage.setItem("token", token);
        localStorage.setItem("userData", JSON.stringify(user));
        localStorage.setItem("last_login_time", Date.now().toString()); // Track login time for grace period
        
        // Verify save immediately
        const savedToken = localStorage.getItem("token");
        const savedUser = localStorage.getItem("userData");
        console.log("[LOGIN] ✅ Token saved:", !!savedToken);
        console.log("[LOGIN] ✅ UserData saved:", !!savedUser);
        console.log("[LOGIN] Token matches?", savedToken === token);
        
        if (!savedToken || savedToken !== token) {
          throw new Error("Failed to save token to localStorage");
        }
      } catch (storageError) {
        console.error("[LOGIN] ❌ localStorage error:", storageError);
        throw new Error("Failed to save authentication data. Please check your browser settings.");
      }
      
      // Update state AFTER localStorage is confirmed saved
      setToken(token);
      setUser(user);
      setLoading(false);

      console.log("[LOGIN] ✅ State updated");
      console.log("[LOGIN] ✅ Login successful!");
      console.log("[LOGIN] Token:", token.substring(0, 30) + '...');
      console.log("[LOGIN] User:", user);

      return { success: true, user };

    } catch (err) {
      console.error("[LOGIN] ====== LOGIN ERROR ======");
      console.error("[LOGIN] Error object:", err);
      console.error("[LOGIN] Error response:", err.response?.data);
      console.error("[LOGIN] Error message:", err.message);
      console.error("[LOGIN] Error code:", err.code);
      console.error("[LOGIN] Request URL:", err.config?.url);
      console.error("[LOGIN] Base URL:", err.config?.baseURL);
      console.error("[LOGIN] Full URL:", `${err.config?.baseURL || ''}${err.config?.url || ''}`);
      
      // Log network errors specifically
      if (err.code === 'ERR_NETWORK' || err.message?.includes('Network Error') || err.message?.includes('ERR_NAME_NOT_RESOLVED')) {
        console.error("[LOGIN] ❌ Network Error - Backend may be unreachable or URL is incorrect");
        console.error("[LOGIN] Attempted URL:", `${err.config?.baseURL || ''}${err.config?.url || ''}`);
        console.error("[LOGIN] Please check REACT_APP_API_URL environment variable");
      }
      
      setLoading(false);
      
      const errorData = err.response?.data || {};
      return { 
        success: false, 
        message: errorData.message || err.message || "Login failed",
        requiresSubscription: errorData.requiresSubscription || false,
        suspended: errorData.suspended || false,
        pending: errorData.pending || false,
        subscriptionExpired: errorData.subscriptionExpired || false,
        email: errorData.email || null,
        expired: errorData.expired || false
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await apiClient.post('/auth/register', userData);
      const { token: newToken, user: newUser } = response.data;
      
      if (newToken) {
        localStorage.setItem("token", newToken);
        setToken(newToken);
        
        const decoded = jwtDecode(newToken);
        const userDataFromToken = newUser || {
          id: decoded.userId || decoded.id,
          email: decoded.email,
          name: decoded.name,
          role: decoded.role || 'user',
          type: decoded.type || 'user',
          ...decoded
        };
        
        setUser(userDataFromToken);
      }
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.message || 'Registration failed' 
      };
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, register, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
